import { useCallback, useState } from 'react';
import { errorText } from './policyEdit';

// The state of one edit dialog and one delete confirmation, for a tab. `onSave(body)` and `onDelete(id)` come from the page
// (they call the API and reload); a failure stays in the dialog as the API's own message, so the person can fix it and try again.
//   editor.form      null when closed, else the form being edited
//   editor.target    the document being changed (null when adding)
// onDelete(id, account) gets the account only when it is not the page's.
export function useEditor({ onSave, onDelete }) {
  const [editing, setEditing] = useState(null);       // { form, target }
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null);     // { id, text }
  const [busy, setBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const open = useCallback((form, target = null) => { setError(''); setEditing({ form, target }); }, []);
  const close = useCallback(() => { setEditing(null); setError(''); }, []);
  const change = useCallback((patch) => setEditing((e) => (e ? { ...e, form: { ...e.form, ...patch } } : e)), []);
  const replace = useCallback((form) => setEditing((e) => (e ? { ...e, form } : e)), []);

  const save = useCallback(async (build) => {
    setSaving(true);
    setError('');
    try {
      await onSave(build(editing.form));
      setEditing(null);
    } catch (ex) {
      setError(errorText(ex));
    } finally {
      setSaving(false);
    }
  }, [editing, onSave]);

  // `account` is only needed for a document that belongs to another account than the page's (a global definition is account *).
  const askDelete = useCallback((id, text, account) => { setDeleteError(''); setDeleting({ id, text, account }); }, []);
  const cancelDelete = useCallback(() => { setDeleting(null); setDeleteError(''); }, []);
  const confirmDelete = useCallback(async () => {
    setBusy(true);
    setDeleteError('');
    try {
      await onDelete(deleting.id, deleting.account);
      setDeleting(null);
    } catch (ex) {
      setDeleteError(errorText(ex));
    } finally {
      setBusy(false);
    }
  }, [deleting, onDelete]);

  return { editing, saving, error, open, close, change, replace, save, deleting, busy, deleteError, askDelete, cancelDelete, confirmDelete };
}

export default useEditor;
